import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';

export { absoluteUrl } from '../components/work/workUpload';

const api = () => process.env.REACT_APP_API_URL || '';
const base = () => `${api()}/api/v3/work-files`;
const config = () => ({ headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } });

/**
 * Working folders (rev6 Part 3). Errors keep the server's { code, message, ...details } body on
 * error.response.data.
 */
const WorkFileService = {
    page: async (contentType, contentId, lang) =>
        (await axios.get(`${base()}/folders/${contentType}/${contentId}`, { ...config(), params: { lang } })).data,

    // Save SRT: the languages this person can save, and { languageKey, level, mode }
    srt: async (videoId) => (await axios.get(`${base()}/srt/${videoId}`, config())).data,
    saveSrt: async (videoId, body) => (await axios.post(`${base()}/srt/${videoId}`, body, config())).data,

    startUpload: async (body) => (await axios.post(`${base()}/uploads`, body, config())).data,
    uploadState: async (uploadId) => (await axios.get(`${base()}/uploads/${uploadId}`, config())).data,
    finishUpload: async (uploadId, mode) =>
        (await axios.post(`${base()}/uploads/${uploadId}/finish`, { mode }, config())).data,
    cancelUpload: async (uploadId) => axios.delete(`${base()}/uploads/${uploadId}`, config()),
    /** While the page unloads: fetch keepalive carries the auth headers, sendBeacon can't. */
    cancelUploadKeepalive: (uploadId) =>
        fetch(`${base()}/uploads/${uploadId}`, { method: 'DELETE', keepalive: true, headers: config().headers }),

    remove: async (fileId) => axios.delete(`${base()}/${fileId}`, config()),
    removeKeepalive: (fileId) =>
        fetch(`${base()}/${fileId}`, { method: 'DELETE', keepalive: true, headers: config().headers }),

    downloadLink: async (fileId) => (await axios.get(`${base()}/${fileId}/download`, config())).data,
    playLink: async (fileId) => (await axios.get(`${base()}/${fileId}/play`, config())).data,

    // New Dubbing Audio from a task (6d): who sees the button, the audio to pick, the latest run
    conversion: async (taskId) => (await axios.get(`${base()}/conversions/tasks/${taskId}`, config())).data,
    /** form: one of fileId, googleDriveUrl or audioFile, and voiceMixingRate. */
    convert: async (taskId, form) =>
        (await axios.post(`${base()}/conversions/tasks/${taskId}`, form, { headers: getAuthHeaders() })).data,

    // Public links (PMs and admins)
    shareLinks: async (contentType, contentId) =>
        (await axios.get(`${base()}/folders/${contentType}/${contentId}/share-links`, config())).data,
    createShareLink: async (contentType, contentId, body) =>
        (await axios.post(`${base()}/folders/${contentType}/${contentId}/share-links`, body, config())).data,
    turnOffShareLink: async (contentType, contentId, linkId) =>
        axios.delete(`${base()}/folders/${contentType}/${contentId}/share-links/${linkId}`, config()),
};

/** The address people open without logging in. */
export const folderLink = (token) => `${window.location.origin}/f/${token}`;

/**
 * The same calls as WorkFileService, for someone holding a link (/f/:token): no login, and the
 * link decides the video. uploaderName goes with every upload, since there is no account.
 */
export const linkFolderService = (token) => {
    const at = () => `${api()}/api/v3/public/work-folders/${encodeURIComponent(token)}`;
    const json = { headers: { 'Content-Type': 'application/json' } };
    return {
        page: async () => (await axios.get(at())).data,
        startUpload: async (body) => (await axios.post(`${at()}/uploads`, body, json)).data,
        uploadState: async (uploadId) => (await axios.get(`${at()}/uploads/${uploadId}`)).data,
        finishUpload: async (uploadId, mode) => (await axios.post(`${at()}/uploads/${uploadId}/finish`, { mode }, json)).data,
        cancelUpload: async (uploadId) => axios.delete(`${at()}/uploads/${uploadId}`),
        cancelUploadKeepalive: (uploadId) => fetch(`${at()}/uploads/${uploadId}`, { method: 'DELETE', keepalive: true }),
        downloadLink: async (fileId) => (await axios.get(`${at()}/files/${fileId}/download`)).data,
        playLink: async (fileId) => (await axios.get(`${at()}/files/${fileId}/play`)).data,
    };
};

/** The server's error code (NAME_TAKEN, FOLDER_LOCKED, ...), if any. */
export const workErrorCode = (error) => error?.response?.data?.code;

export const workErrorMessage = (error) =>
    error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.';

export default WorkFileService;
